import React, { useContext, useState, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Row, Col, Modal, Form } from "react-bootstrap";
import PageLayout from "../../layouts/PageLayout";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Swal from "sweetalert2";

const decodeDescription = (html) => {
    if (!html) return "";
    const textarea = document.createElement("textarea");
    textarea.innerHTML = html;
    let decoded = textarea.value;
    textarea.innerHTML = decoded;
    decoded = textarea.value;
    decoded = decoded.replace(/<pre[^>]*>([\s\S]*?)<\/pre>/gi, "$1");
    decoded = decoded
        .replace(/&nbsp;/gi, " ")
        .replace(/\r?\n|\r/g, " ")
        .replace(/\s{2,}/g, " ")
        .trim();
    return decoded;
};

const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
        toast.style.padding = "16px 20px";
        toast.addEventListener("mouseenter", Swal.stopTimer);
        toast.addEventListener("mouseleave", Swal.resumeTimer);
    }
});

export default function CheckerProductView() {
    const navigate = useNavigate();
    const { t } = useContext(TranslatorContext);
    const location = useLocation();
    const passedItem = location.state?.item;

    const [selectedImage, setSelectedImage] = useState(passedItem?.card_img);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [submittingDecision, setSubmittingDecision] = useState(false);

    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);

    const [editingField, setEditingField] = useState(null);

    const [formData, setFormData] = useState({
        title: passedItem?.title || "",
        category_id: passedItem?.category_id || passedItem?.category?.id || "",
        brand: passedItem?.brand?.id || passedItem?.brand || "",
        selling_style: passedItem?.selling_style || "Fixed",
        buying_price: passedItem?.buying_price || 0,
        sell_price_dollar: passedItem?.sell_price_dollar || 0,
        shipping_charges_dollar: passedItem?.shipping_charges_dollar || 0,
        product_qty: passedItem?.product_qty || 0,
        short_description: passedItem?.short_description || "",
        description: passedItem?.description ? decodeDescription(passedItem.description) : ""
    });

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/checker/products/initial-data`,
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem("authToken")}`
                        }
                    }
                );
                if (response.data) {
                    setCategories(response.data.data.categories || []);
                    setBrands(response.data.data.brands || []);
                }
            } catch (error) {
                console.error("Failed to fetch initial data", error);
            }
        };

        fetchInitialData();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDescriptionChange = (content) => {
        setFormData((prev) => ({
            ...prev,
            description: content
        }));
    };

    const submitDecision = async (decisionType, reason = "") => {
        try {
            setSubmittingDecision(true);
            const payload = new FormData();

            payload.append("type", decisionType);
            payload.append("reason", reason);
            payload.append("category_id", formData.category_id);
            payload.append("brand", formData.brand);
            payload.append("selling_style", formData.selling_style);
            payload.append("title", formData.title);
            payload.append("description", formData.description);
            payload.append("short_description", formData.short_description);
            payload.append("buying_price", formData.buying_price);
            payload.append("sell_price_dollar", formData.sell_price_dollar);
            payload.append("shipping_charges_dollar", formData.shipping_charges_dollar);
            payload.append("product_qty", formData.product_qty);
            payload.append("_method", "PUT");

            await axios.post(
                `${process.env.REACT_APP_API_URL}/checker/products/${passedItem?.id}`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`
                    }
                }
            );

            Toast.fire({
                icon: "success",
                title: decisionType === "approve"
                    ? "Product approved successfully!"
                    : "Product rejected successfully!"
            });

            if (decisionType === "reject") {
                setShowRejectModal(false);
                setRejectReason("");
            }

            navigate("/checker-products");
        } catch (error) {
            console.error(error);
            Toast.fire({
                icon: "error",
                title: error?.response?.data?.message || `Unable to ${decisionType} product`
            });
        } finally {
            setSubmittingDecision(false);
        }
    };

    const handleApprove = () => {
        Swal.fire({
            title: "Approve Product?",
            text: "Are you sure you want to approve this product?",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#198754",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, Approve!",
            didOpen: () => {
                const confirmBtn = Swal.getConfirmButton();
                const cancelBtn = Swal.getCancelButton();

                if (confirmBtn) {
                    confirmBtn.style.minHeight = "48px";
                    confirmBtn.style.padding = "12px 28px";
                    confirmBtn.style.fontSize = "15px";
                    confirmBtn.style.fontWeight = "600";
                }
                if (cancelBtn) {
                    cancelBtn.style.minHeight = "48px";
                    cancelBtn.style.padding = "12px 28px";
                    cancelBtn.style.fontSize = "15px";
                    cancelBtn.style.fontWeight = "600";
                }
            }
        }).then((result) => {
            if (result.isConfirmed) {
                submitDecision("approve", "");
            }
        });
    };

    const handleReject = () => {
        if (!rejectReason.trim()) {
            Toast.fire({
                icon: "warning",
                title: "Please enter rejection reason"
            });
            return;
        }
        submitDecision("reject", rejectReason.trim());
    };

    const renderEditControl = (fieldKey, type = "text", options = []) => {
        if (editingField === fieldKey) {
            if (type === "select") {
                return (
                    <div className="d-flex align-items-center gap-2 mt-1">
                        <Form.Select
                            size="sm"
                            name={fieldKey}
                            value={formData[fieldKey]}
                            onChange={handleInputChange}
                        >
                            <option value="">Select Option</option>
                            {options.map((opt) => (
                                <option key={opt.id || opt.value} value={opt.id || opt.value}>
                                    {opt.name || opt.label}
                                </option>
                            ))}
                        </Form.Select>
                        <i
                            className="material-icons text-success cursor-pointer"
                            onClick={() => setEditingField(null)}
                            style={{ cursor: "pointer" }}
                        >
                            check
                        </i>
                    </div>
                );
            }

            if (type === "textarea") {
                return (
                    <div className="d-flex align-items-center gap-2 mt-1 w-100">
                        <Form.Control
                            as="textarea"
                            rows={3}
                            name={fieldKey}
                            value={formData[fieldKey]}
                            onChange={handleInputChange}
                        />
                        <i
                            className="material-icons text-success cursor-pointer"
                            onClick={() => setEditingField(null)}
                            style={{ cursor: "pointer" }}
                        >
                            check
                        </i>
                    </div>
                );
            }

            return (
                <div className="d-flex align-items-center gap-2 mt-1">
                    <Form.Control
                        size="sm"
                        type={type}
                        name={fieldKey}
                        value={formData[fieldKey]}
                        onChange={handleInputChange}
                    />
                    <i
                        className="material-icons text-success cursor-pointer"
                        onClick={() => setEditingField(null)}
                        style={{ cursor: "pointer" }}
                    >
                        check
                    </i>
                </div>
            );
        }

        return (
            <i
                className="material-icons ms-2 text-primary"
                onClick={() => setEditingField(fieldKey)}
                style={{ cursor: "pointer", fontSize: "18px" }}
            >
                edit
            </i>
        );
    };

    const getSelectedName = (list, id) => {
        const found = list.find((item) => String(item.id) === String(id));
        return found ? found.name : id;
    };

    return (
        <PageLayout>
            <div className="mc-card mb-4">
                <div className="mc-breadcrumb">
                    <h3 className="mc-breadcrumb-title">{t("product_view")}</h3>
                    <ul className="mc-breadcrumb-list">
                        <li className="mc-breadcrumb-item">
                            <Link to="#" className="mc-breadcrumb-link">{t("home")}</Link>
                        </li>
                        <li className="mc-breadcrumb-item">
                            <Link to="#" className="mc-breadcrumb-link">{t("products")}</Link>
                        </li>
                        <li className="mc-breadcrumb-item">{t("product_view")}</li>
                    </ul>
                </div>
            </div>

            <div className="mc-card p-lg-4">
                <Row>
                    <Col xl={5}>
                        <h6 className="mc-divide-title mb-4">{passedItem?.product_code}</h6>
                        <div className="mc-product-view-gallery">
                            <img
                                src={process.env.REACT_APP_IMG_URL + selectedImage}
                                alt="product"
                                className="main-product-image"
                            />
                            <img
                                src={process.env.REACT_APP_IMG_URL + passedItem?.card_img}
                                alt="product"
                                onClick={() => setSelectedImage(passedItem?.card_img)}
                                className="product-thumbnail"
                            />
                            {passedItem?.images?.map((img, index) => (
                                <img
                                    key={index}
                                    src={process.env.REACT_APP_IMG_URL + img.image}
                                    alt="product"
                                    onClick={() => setSelectedImage(img.image)}
                                    className="product-thumbnail"
                                />
                            ))}
                        </div>
                    </Col>

                    <Col xl={7}>
                        <h6 className="mc-divide-title mb-4">{t("product_details")}</h6>
                        <div className="mc-product-view-info-group">
                            <div className="d-flex align-items-center mb-2">
                                <h2 className="mc-product-view-info-title m-0">{formData.title}</h2>
                                {renderEditControl("title", "text")}
                            </div>

                            <div className="mc-product-view-meta">
                                <i className="material-icons">inventory_2</i>
                                <h5>Product Code</h5>
                                <span>:</span>
                                <p>{passedItem?.product_code}</p>
                            </div>

                            <div className="mc-product-view-meta align-items-center">
                                <i className="material-icons">category</i>
                                <h5>Category</h5>
                                <span>:</span>
                                <p className="mb-0">{getSelectedName(categories, formData.category_id)}</p>
                                {renderEditControl("category_id", "select", categories)}
                            </div>

                            <div className="mc-product-view-meta align-items-center">
                                <i className="material-icons">branding_watermark</i>
                                <h5>Brand</h5>
                                <span>:</span>
                                <p className="mb-0">{getSelectedName(brands, formData.brand)}</p>
                                {renderEditControl("brand", "select", brands)}
                            </div>

                            <div className="mc-product-view-meta align-items-center">
                                <i className="material-icons">sell</i>
                                <h5>Selling Style</h5>
                                <span>:</span>
                                <p className="mb-0">{formData.selling_style}</p>
                                {renderEditControl("selling_style", "select", [
                                    { value: "Fixed", label: "Fixed" },
                                    { value: "Auction", label: "Auction" }
                                ])}
                            </div>

                            <div className="mc-product-view-meta align-items-center">
                                <i className="material-icons">payments</i>
                                <h5>Buying Price PKR</h5>
                                <span>:</span>
                                <p className="mb-0">{formData.buying_price}</p>
                                {renderEditControl("buying_price", "number")}
                            </div>

                            <div className="mc-product-view-meta align-items-center">
                                <i className="material-icons">attach_money</i>
                                <h5>Buying Price USD</h5>
                                <span>:</span>
                                <p className="mb-0">${formData.sell_price_dollar}</p>
                                {renderEditControl("sell_price_dollar", "number")}
                            </div>

                            <div className="mc-product-view-meta align-items-center">
                                <i className="material-icons">local_shipping</i>
                                <h5>Shipping Charges USD</h5>
                                <span>:</span>
                                <p className="mb-0">${formData.shipping_charges_dollar}</p>
                                {renderEditControl("shipping_charges_dollar", "number")}
                            </div>

                            <div className="mc-product-view-meta align-items-center">
                                <i className="material-icons">confirmation_number</i>
                                <h5>Quantity</h5>
                                <span>:</span>
                                <p className="mb-0">{formData.product_qty}</p>
                                {renderEditControl("qty", "number")}
                            </div>

                            <div className="mc-product-view-meta align-items-center">
                                <i className="material-icons">description</i>
                                <h5>Short Description</h5>
                                <span>:</span>
                                <p className="mb-0">{formData.short_description}</p>
                                {renderEditControl("short_description", "textarea")}
                            </div>

                            <div className="product-checker-actions mt-4">
                                <button
                                    type="button"
                                    className="btn btn-success me-2"
                                    onClick={handleApprove}
                                    disabled={submittingDecision}
                                >
                                    <i className="material-icons align-middle me-1">check</i>
                                    Approve
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={() => setShowRejectModal(true)}
                                    disabled={submittingDecision}
                                >
                                    <i className="material-icons align-middle me-1">close</i>
                                    Reject
                                </button>
                            </div>
                        </div>
                    </Col>

                    <Col xl={12}>
                        <div className="d-flex align-items-center mt-5 mb-4">
                            <h6 className="mc-divide-title m-0 me-2">{t("product_description")}</h6>
                        </div>

                        <div className="mc-product-description">
                            <ReactQuill
                                theme="snow"
                                value={formData.description}
                                onChange={handleDescriptionChange}
                            />
                        </div>
                    </Col>
                </Row>
            </div>

            <Modal
                show={showRejectModal}
                onHide={() => {
                    if (!submittingDecision) {
                        setShowRejectModal(false);
                    }
                }}
                centered
            >
                <Modal.Header closeButton={!submittingDecision} className="m-3">
                    <Modal.Title>Reject Product</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className="mb-3">
                        <label className="form-label">Rejection Reason</label>
                        <textarea
                            className="form-control"
                            rows={5}
                            placeholder="Enter reason for rejecting this product..."
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            disabled={submittingDecision}
                        />
                    </div>
                </Modal.Body>

                <Modal.Footer className="m-3">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                            setShowRejectModal(false);
                            setRejectReason("");
                        }}
                        disabled={submittingDecision}
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        className="btn btn-danger"
                        onClick={handleReject}
                        disabled={submittingDecision || !rejectReason.trim()}
                    >
                        {submittingDecision ? (
                            "Rejecting..."
                        ) : (
                            <>
                                <i className="material-icons me-1 align-middle">close</i>
                                Reject Product
                            </>
                        )}
                    </button>
                </Modal.Footer>
            </Modal>
        </PageLayout>
    );
}